// Utility function: Normalize Unicode characters to ASCII equivalents
const normalizeUnicode = (str: string | null | undefined) => {
  if (!str) return '';

  // Vietnamese character mapping
  const vietnameseMap: Record<string, string> = {
    à: 'a',
    á: 'a',
    ạ: 'a',
    ả: 'a',
    ã: 'a',
    â: 'a',
    ầ: 'a',
    ấ: 'a',
    ậ: 'a',
    ẩ: 'a',
    ẫ: 'a',
    ă: 'a',
    ằ: 'a',
    ắ: 'a',
    ặ: 'a',
    ẳ: 'a',
    ẵ: 'a',
    è: 'e',
    é: 'e',
    ẹ: 'e',
    ẻ: 'e',
    ẽ: 'e',
    ê: 'e',
    ề: 'e',
    ế: 'e',
    ệ: 'e',
    ể: 'e',
    ễ: 'e',
    ì: 'i',
    í: 'i',
    ĩ: 'i',
    ị: 'i',
    ỉ: 'i',
    ò: 'o',
    ó: 'o',
    ọ: 'o',
    ỏ: 'o',
    õ: 'o',
    ô: 'o',
    ồ: 'o',
    ố: 'o',
    ộ: 'o',
    ổ: 'o',
    ỗ: 'o',
    ơ: 'o',
    ờ: 'o',
    ớ: 'o',
    ợ: 'o',
    ở: 'o',
    ỡ: 'o',
    ù: 'u',
    ú: 'u',
    ụ: 'u',
    ủ: 'u',
    ũ: 'u',
    ư: 'u',
    ừ: 'u',
    ứ: 'u',
    ự: 'u',
    ử: 'u',
    ữ: 'u',
    ỳ: 'y',
    ý: 'y',
    ỵ: 'y',
    ỷ: 'y',
    ỹ: 'y',
    đ: 'd',
    // Uppercase variants
    À: 'a',
    Á: 'a',
    Ạ: 'a',
    Ả: 'a',
    Ã: 'a',
    Â: 'a',
    Ầ: 'a',
    Ấ: 'a',
    Ậ: 'a',
    Ẩ: 'a',
    Ẫ: 'a',
    Ă: 'a',
    Ằ: 'a',
    Ắ: 'a',
    Ặ: 'a',
    Ẳ: 'a',
    Ẵ: 'a',
    È: 'e',
    É: 'e',
    Ẹ: 'e',
    Ẻ: 'e',
    Ẽ: 'e',
    Ê: 'e',
    Ề: 'e',
    Ế: 'e',
    Ệ: 'e',
    Ể: 'e',
    Ễ: 'e',
    Ì: 'i',
    Í: 'i',
    Ĩ: 'i',
    Ị: 'i',
    Ỉ: 'i',
    Ò: 'o',
    Ó: 'o',
    Ọ: 'o',
    Ỏ: 'o',
    Õ: 'o',
    Ô: 'o',
    Ồ: 'o',
    Ố: 'o',
    Ộ: 'o',
    Ổ: 'o',
    Ỗ: 'o',
    Ơ: 'o',
    Ờ: 'o',
    Ớ: 'o',
    Ợ: 'o',
    Ở: 'o',
    Ỡ: 'o',
    Ù: 'u',
    Ú: 'u',
    Ụ: 'u',
    Ủ: 'u',
    Ũ: 'u',
    Ư: 'u',
    Ừ: 'u',
    Ứ: 'u',
    Ự: 'u',
    Ử: 'u',
    Ữ: 'u',
    Ỳ: 'y',
    Ý: 'y',
    Ỵ: 'y',
    Ỷ: 'y',
    Ỹ: 'y',
    Đ: 'd',
  };

  // Replace Vietnamese characters
  let normalized = [...str].map(char => vietnameseMap[char] ?? char).join('');

  // Use NFD normalization to separate base characters from diacritics, then remove diacritics
  // This handles any remaining Unicode characters from other languages
  normalized = normalized.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');

  return normalized;
};

/**
 * Generate a URL-friendly slug from a title
 */
export function generateSlug(title: string): string {
  if (!title || typeof title !== 'string') return '';

  // Ensure title is actually a string to avoid unsafe operations
  const safeTitle = String(title);
  // First normalize Unicode characters to ASCII
  const normalizedName = normalizeUnicode(safeTitle);

  const slug = normalizedName
    .toLowerCase()
    .replaceAll(/[^\da-z]+/g, '-')
    .replaceAll(/^-+|-+$/g, '');

  return `${slug}`;
}

/**
 * Get current language from URL
 * @returns Current language code or 'en' as default
 */
export function getCurrentLanguageFromUrl(): string {
  if (typeof window === 'undefined') return 'en';

  const pathParts = window.location.pathname.split('/').filter(Boolean);
  if (pathParts.length === 0) return 'en';

  const supportedLangs = ['en', 'ko', 'zh', 'ja', 'vi', 'ru', 'mn', 'ar', 'th', 'id', 'es'];
  const firstPart = pathParts[0]?.toLowerCase() ?? '';

  return supportedLangs.includes(firstPart) ? firstPart : 'en';
}

/**
 * Generic URL parsing utility for extracting identifiers from slug-based URLs
 */
export function parseSlugUrl(urlPath: string): string | null {
  if (!urlPath) return null;

  const parts = urlPath.split('-');
  const lastPart = parts.at(-1);
  return lastPart ?? null;
}

/**
 * Generic URL creation utility for entity pages
 */
export function createEntityUrl(
  entityType: string,
  name: string,
  id: string,
  lang?: string
): string {
  const currentLang = lang ?? getCurrentLanguageFromUrl();
  const slug = generateSlug(name);
  const shortId = id.split('-')[0]; // Take first part of UUID
  return `/${currentLang}/${entityType}/${slug}-${shortId}`;
}
